import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDashboardEvents } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { useRegistrations } from '../hooks/useRegistrations';
import * as dashboardService from '../services/dashboardService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getStatusLabel, getStatusBadgeClasses } from '../utils/eventStatusMapping';
import ExportRegistrationsModal from '../components/ExportRegistrationsModal';
import EventRegistrationsTab from '../components/Events/EventRegistrationsTab';

const { 
  FiCalendar, FiClock, FiUsers, FiDollarSign, FiExternalLink, 
  FiEdit, FiShare2, FiCopy, FiBarChart3, FiSettings 
} = FiIcons;

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { events } = useDashboardEvents();
  const { user, role } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  const [overviewStats, setOverviewStats] = useState(null);
  const [overviewStatsLoading, setOverviewStatsLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const event = events.find(e => e.id === eventId);

  const registrationQueryOptions = useMemo(
    () => ({ role, userId: user?.id, eventIds: eventId ? [eventId] : [] }),
    [role, user?.id, eventId]
  );

  const { data: registrationsResult, isLoading: registrationsLoading } = useRegistrations(
    registrationQueryOptions,
    { enabled: Boolean(eventId && user?.id) }
  );
  const registrations = registrationsResult?.data ?? [];
  const registrationsError = registrationsResult?.error;

  // Event overview stats: paid registrations, revenue, conversion rate from leads (payment_status = 'paid')
  useEffect(() => {
    if (!eventId || !user?.id) return;
    setOverviewStatsLoading(true);
    dashboardService
      .getEventOverviewStats(eventId, { role, userId: user.id })
      .then(({ data, error }) => {
        if (error) {
          console.error('Event overview stats error:', error);
          setOverviewStats(null);
        } else {
          setOverviewStats(data);
        }
      })
      .finally(() => setOverviewStatsLoading(false));
  }, [eventId, user?.id, role]);

  if (!event) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h1>
        <p className="text-gray-600">The event you're looking for doesn't exist.</p>
        <Link to="/events" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
          Back to Events
        </Link>
      </div>
    );
  }

  const landingPageUrl = `${window.location.origin}/landing/${event.id}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(landingPageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'registrations', label: 'Registrations' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'settings', label: 'Settings' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-4">
              <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClasses(event.status)}`}>
                {getStatusLabel(event.status)}
              </span>
            </div>
            
            <p className="text-gray-600 mb-4">{event.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{event.date}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="font-medium">{event.time}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUsers} className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Registered</p>
                  <p className="font-medium">{event.registrations}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Price</p>
                  <p className="font-medium">{event.price === 0 ? 'Free' : `$${event.price}`}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Link
              to={`/landing/${event.id}`}
              target="_blank"
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
              <span>View Landing</span>
            </Link>
            <button
              onClick={() => navigate(`/dashboard/events/${event.id}/edit`)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiEdit} className="w-4 h-4" />
              <span>Edit Event</span>
            </button>
          </div>
        </div>
      </div>

      {/* Share Event */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Share Event</h2>
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <input
              type="text"
              value={landingPageUrl}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>
          <button
            onClick={copyToClipboard}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiCopy} className="w-4 h-4" />
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
            <SafeIcon icon={FiShare2} className="w-4 h-4" />
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Total Registrations</h3>
                  {overviewStatsLoading ? (
                    <p className="text-2xl font-bold text-blue-600">…</p>
                  ) : (
                    <p className="text-2xl font-bold text-blue-600">{overviewStats?.total_registrations ?? overviewStats?.paid_registrations ?? 0}</p>
                  )}
                  <p className="text-sm text-blue-600">Completed registrations (Step 2) for this event</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">Revenue Generated</h3>
                  {overviewStatsLoading ? (
                    <p className="text-2xl font-bold text-green-600">…</p>
                  ) : (
                    <p className="text-2xl font-bold text-green-600">${(overviewStats?.revenue ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  )}
                  <p className="text-sm text-green-600">Sum of payments received</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-900 mb-2">Conversion Rate</h3>
                  {overviewStatsLoading ? (
                    <p className="text-2xl font-bold text-purple-600">…</p>
                  ) : (
                    <p className="text-2xl font-bold text-purple-600">{overviewStats?.conversion_rate ?? 0}%</p>
                  )}
                  <p className="text-sm text-purple-600">Paid leads ÷ total leads for this event</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Event Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Meeting Information</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><strong>Zoom Link:</strong> {event.zoomLink || 'Not set'}</p>
                      <p><strong>Duration:</strong> {event.duration || 60} minutes</p>
                      <p><strong>Max Attendees:</strong> {event.maxAttendees || 'Unlimited'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Settings</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><strong>Tiered Pricing:</strong> {event.tieredPricing ? 'Enabled' : 'Disabled'}</p>
                      <p><strong>Created:</strong> {new Date(event.createdAt || Date.now()).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'registrations' && (
            <EventRegistrationsTab
              eventId={eventId}
              eventAgentId={event.agentId}
              registrations={registrations}
              registrationsLoading={registrationsLoading}
              registrationsError={registrationsError}
              authOptions={registrationQueryOptions}
              onExportClick={() => setExportModalOpen(true)}
            />
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="font-semibold text-gray-900">Event Analytics</h3>
              <div className="bg-blue-50 rounded-lg p-6">
                <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <p className="text-center text-blue-800">
                  Advanced analytics and reporting features will be available here, including registration trends, 
                  conversion rates, and audience insights.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="font-semibold text-gray-900">Event Settings</h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <SafeIcon icon={FiSettings} className="w-8 h-8 text-gray-600 mx-auto mb-4" />
                <p className="text-center text-gray-700">
                  Event configuration options, including pricing updates, template changes, 
                  and advanced settings will be available here.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ExportRegistrationsModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        registrations={registrations}
        eventTitle={event?.title ?? 'Event'}
        role={role}
        userId={user?.id}
      />
    </div>
  );
};

export default EventDetails;